/**
 * Loading, refused, failed, empty — four states, never three.
 *
 * THE DISTINCTION THIS COMPONENT EXISTS FOR.
 *
 * A refusal is not an empty list. §14.2 denies a read the Consent Decision
 * Point does not permit, and rendering that as "You have no medications" tells
 * a patient something false about their own care. It is §16.3's rule — `null` ≠
 * `0` ≠ "not done" ≠ "unknown" — applied to a whole screen instead of a value.
 *
 * This is not hypothetical. Diagnosing this platform produced two wrong
 * conclusions in a row from exactly that collapse: a probe doing
 * `bundle?.entry?.length ?? 0` reported every 403 as "0 entries", so a
 * consent-denied cohort looked first like an empty one and then like a gateway
 * hiding data. Neither was true. In a throwaway script that cost an afternoon.
 * On a patient's screen it would be a patient believing they have no
 * prescriptions.
 *
 * So `AccessRefusedError` gets its own branch, says that something is being
 * withheld, and shows the server's own reason — a patient is entitled to know
 * that a door exists even when it is closed to them.
 */

import type { JSX, ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { AccessRefusedError, HttpError, SessionExpiredError } from '../lib/api.js';

interface AsyncProps<T> {
  readonly query: UseQueryResult<readonly T[]>;
  /** What to say when the list is genuinely, permittedly empty. */
  readonly empty: string;
  readonly children: (items: readonly T[]) => ReactNode;
}

const messageFor = (error: unknown): string => {
  if (error instanceof SessionExpiredError) return error.message;
  if (error instanceof HttpError) return error.message;
  return 'Something went wrong loading this. Please try again.';
};

export const Async = <T,>({ query, empty, children }: AsyncProps<T>): JSX.Element => {
  if (query.isLoading) {
    return <p role="status">Loading…</p>;
  }

  if (query.error instanceof AccessRefusedError) {
    return (
      <div role="alert" className="refused">
        <p>
          <strong>This part of your record is not being shown.</strong>
        </p>
        {/*
          The server's reason, verbatim. It names the clause it acted under,
          which is more useful to a patient asking their care team about it
          than anything this component could paraphrase — and paraphrasing a
          refusal is how it turns into "no data".
        */}
        <p>{query.error.reason}</p>
        <p>This is not the same as having no records. Ask your care team.</p>
      </div>
    );
  }

  if (query.error !== null && query.error !== undefined) {
    return (
      <p role="alert" className="error">
        {messageFor(query.error)}
      </p>
    );
  }

  const items = query.data ?? [];
  if (items.length === 0) {
    return <p className="empty">{empty}</p>;
  }

  return <>{children(items)}</>;
};
